import { Button, Card, Grid, Icon, Typography } from '@material-ui/core';
import { observer } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

import { WithdrawContractManualResponseDto } from '../../../core/api/dtos';
import { injectTSDI } from '../../../core/common/tsdi';
import { I18n } from '../../../core/i18n';
import { StudioInformation } from '../../components/inputs/studio-information';
import { SharedContractDissolutionStore } from '../../shared-contract-dissolution-store';
import { BottomPaddedGridItemS } from '../../styles';

const IconWrapper = styled.div`
    font-size: 120px;
    display: flex;
    align-items: center;
    color: green;
    padding: 32px 0 32px 0;
`;

const TopPaddedGridItemS = styled(Grid)`
    padding-top: 20px;
`;

const PaddedCard = styled(Card)`
    padding: 16px;
`;

export interface ManualWithdrawalSuccessProps {
    manualWithdrawal?: WithdrawContractManualResponseDto;
}

export const ManualWithdrawalSuccessPage = observer(
    (props: ManualWithdrawalSuccessProps) => {
        const { formatDate, formatDateTime, translate } = injectTSDI(I18n);
        const studioPublicDto = injectTSDI(
            SharedContractDissolutionStore
        ).getStudio(props.manualWithdrawal?.studioId)?.studioPublicDto;

        return (
            <Grid container>
                <BottomPaddedGridItemS item xs={12}>
                    <PaddedCard variant="outlined">
                        <Grid
                            container
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Grid container item xs={12} sm={9}>
                                <BottomPaddedGridItemS item xs={12}>
                                    <Typography
                                        variant="h5"
                                        data-role="manual-withdrawal-success-header"
                                    >
                                        {translate(
                                            'public.content.withdraw.contract.success'
                                        )}
                                    </Typography>
                                </BottomPaddedGridItemS>
                                <BottomPaddedGridItemS item xs={12}>
                                    <Typography variant="subtitle2">
                                        {translate(
                                            'public.content.withdraw.contract.associate.warning'
                                        )}
                                    </Typography>
                                </BottomPaddedGridItemS>
                                <Grid container item xs={12}>
                                    <BottomPaddedGridItemS item xs={7} md={5}>
                                        <Typography>
                                            {translate(
                                                'public.content.common.firstname'
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={5} md={7}>
                                        <Typography data-role="manual-withdrawal-success-firstname">
                                            {props.manualWithdrawal?.firstname}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={7} md={5}>
                                        <Typography>
                                            {translate(
                                                'public.content.common.lastname'
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={5} md={7}>
                                        <Typography data-role="manual-withdrawal-success-lastname">
                                            {props.manualWithdrawal?.lastname}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={7} md={5}>
                                        <Typography>
                                            {translate(
                                                'public.content.common.dob'
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={5} md={7}>
                                        <Typography data-role="manual-withdrawal-success-date-of-birth">
                                            {formatDate(
                                                props.manualWithdrawal
                                                    ?.dateOfBirth
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={7} md={5}>
                                        <Typography>
                                            {translate(
                                                'public.content.common.email'
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={5} md={7}>
                                        <Typography data-role="manual-withdrawal-success-email-address">
                                            {props.manualWithdrawal
                                                ?.confirmationEmail || '-'}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={7} md={5}>
                                        <Typography>
                                            {translate(
                                                'public.content.common.memberid'
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={5} md={7}>
                                        <Typography data-role="manual-withdrawal-success-customer-number">
                                            {props.manualWithdrawal
                                                ?.customerNumber || '-'}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={7} md={5}>
                                        <Typography>
                                            {translate(
                                                'public.content.common.studio'
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={5} md={7}>
                                        <StudioInformation
                                            studioPublicDto={studioPublicDto}
                                        />
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={7} md={5}>
                                        <Typography>
                                            {translate(
                                                'public.content.withdraw.contract.additional.info'
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={5} md={7}>
                                        <Typography data-role="manual-withdrawal-success-additional-info">
                                            {
                                                props.manualWithdrawal
                                                    ?.additionalInformation
                                            }
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={7} md={5}>
                                        <Typography>
                                            {translate(
                                                'public.content.withdraw.contract.withdrawn.on'
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    <BottomPaddedGridItemS item xs={5} md={7}>
                                        <Typography data-role="manual-withdrawal-success-received-date">
                                            {formatDateTime(
                                                props.manualWithdrawal
                                                    ?.requestReceivedDate
                                            )}
                                        </Typography>
                                    </BottomPaddedGridItemS>
                                    {props.manualWithdrawal
                                        ?.confirmationEmail && (
                                        <TopPaddedGridItemS item xs={12}>
                                            <Typography data-role="manual-withdrawal-success-email">
                                                {translate(
                                                    'public.content.withdraw.contract.confirmation.email',
                                                    props.manualWithdrawal
                                                        .confirmationEmail
                                                )}
                                            </Typography>
                                        </TopPaddedGridItemS>
                                    )}
                                </Grid>
                            </Grid>
                            <Grid
                                item
                                container
                                xs={12}
                                sm={3}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <IconWrapper>
                                    <Icon fontSize="inherit">check_circle</Icon>
                                </IconWrapper>
                            </Grid>
                        </Grid>
                    </PaddedCard>
                </BottomPaddedGridItemS>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={window.print}
                    >
                        {translate('public.content.common.print')}
                    </Button>
                </Grid>
            </Grid>
        );
    }
);
